import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useEntitlements } from '../../queries';
import { Spinner } from '@/shared/ui/atoms/Spinner';
import { EmptyState } from '@/shared/ui/molecules/EmptyState';

type Props = {
  /** Catalog feature code, e.g. "ANALYTICS". */
  feature: string;
  children: ReactNode;
  /** Rendered when the plan lacks the feature. Defaults to an upgrade prompt. */
  fallback?: ReactNode;
};

/**
 * Shows its children only when the organisation's plan includes the feature. It is a UX aid: the backend
 * enforces the same rule (402). If entitlements cannot be loaded, or the platform runs in observe-only mode,
 * the children render and the server decides.
 */
export function FeatureGate({ feature, children, fallback }: Props) {
  const { data, isPending, isError } = useEntitlements();
  const { t } = useTranslation(undefined, { keyPrefix: 'billing.gate' });
  if (isPending) return <Spinner />;
  if (isError || !data || !data.enforced || data.features.includes(feature)) return <>{children}</>;
  if (fallback !== undefined) return <>{fallback}</>;
  return (
    <div className="p-screen">
      <EmptyState title={t('title', { feature: t(`features.${feature}`, { defaultValue: feature }) })} hint={t('hint', { plan: data.planName })} />
    </div>
  );
}
